import React from 'react';
import { Upload, FileText, Loader2, MailCheck, ArrowRight, Undo2, SkipForward, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Field, Input } from '../ui/Field';
import { pillPrimary, pillSecondary, pillGhost } from '../ui/pills';
import { PIPELINE_STAGE_LABELS } from './pipelineStages';
import { PipelineTransition } from './pipelineTransitions';
import { PlaquetteEmailFields, PlaquetteEmail } from './PlaquetteEmailFields';
import { moduleApi } from '../../lib/api';
import { DevisEmailFields, DevisEmail } from './DevisEmailFields';
import { BriefQuestionsForm, BriefQuestion } from './BriefQuestionsForm';
import { ClientDemarcheAnswers } from './ClientDemarcheAnswers';
import { DevisSigneApercu } from './DevisSigneApercu';
import { FicheDocument } from './PipelineDocuments';

/**
 * Panneau de l'étape active — remplace la modale `PipelineActionModal` (2026-09-01, demande d'Emeni :
 * « assistant par étapes en pleine page, sans popup »). Même logique de validation, mêmes champs, mais
 * intégrés directement dans le corps de la page, avec Précédent / Passer / Suivant en bas. C'est le serveur
 * qui reste seul juge (PIPELINE_TRANSITIONS côté functions/api/[[path]].js) : le blocage ici est un confort,
 * pas une sécurité.
 *
 * Refonte design lot 9 (maquette 18a) : le panneau EST le bandeau « Ce qui bloque l'étape en cours » —
 * jeton « attention », icône, verbe de l'étape et son explication, puis les champs dans une carte blanche.
 * L'abandon du dossier n'y est plus : il vit dans le pied de la fiche (`DossierPipelinePage`).
 */
export function PipelineActionPanel({
    label, transition, skipTransition, accepteTransition, backLabel,
    saving, error, destinataire, destinataireEstReferent, dossierId,
    onConfirm, onBack, apercuDevisSigne, blocage, actionBlocage,
}: {
    label: string;
    transition: PipelineTransition;
    skipTransition?: PipelineTransition | null;
    /** Devis envoyé et accepté hors plateforme : saute l'envoi / l'exemplaire signé obligatoires. */
    accepteTransition?: PipelineTransition | null;
    backLabel?: string | null;
    saving: boolean;
    error: string;
    /** Adresse réelle de l'envoi, affichée avant de cliquer : référent s'il en a une, entreprise sinon. */
    destinataire?: string;
    destinataireEstReferent?: boolean;
    /** Requis quand `transition.embedsBrief` : sert à charger et enregistrer le brief de CE dossier. */
    dossierId?: string;
    onConfirm: (payload: Record<string, any>) => void;
    onBack?: () => void;
    /** Le vrai blocage, chiffré, quand la page le connaît (« 12 convives sur 108 n'ont pas encore choisi
     *  leur plat… ») : remplace la note générique du rulebook dans le bandeau. */
    blocage?: React.ReactNode;
    /** Le bouton du bandeau, à droite du texte (« Relancer les convives »). */
    actionBlocage?: React.ReactNode;
    /** Quand le devis signé a déjà été déposé : son aperçu remplace la phrase d'explication, directement
     *  dans ce panneau — demande d'Emeni (2026-09-02), pour ne rien renvoyer vers une autre section. */
    apercuDevisSigne?: { dossierId: string; document: FicheDocument } | null;
}) {
    const [file, setFile] = React.useState<File | null>(null);
    const [values, setValues] = React.useState<Record<string, string>>({});
    const [reading, setReading] = React.useState(false);
    // Un document trop lourd est refusé DÈS LE CHOIX du fichier, pas après un téléversement de plusieurs
    // Mo suivi d'un refus du serveur. La base n'est plus la limite (les documents de privatisation sont
    // stockés en morceaux depuis le 30/08) : ce qui reste limité, c'est la boîte mail du destinataire, qui
    // refuse la pièce jointe au-delà d'une dizaine de Mo.
    const [fileError, setFileError] = React.useState('');
    // L'envoi de la plaquette est la seule étape qui parle au client : elle porte son propre message et sa
    // Démarche de destination. Coché par défaut — envoyer la plaquette EST le geste de cette étape ;
    // décocher sert au cas où elle a déjà été envoyée depuis une messagerie.
    const [courriel, setCourriel] = React.useState<PlaquetteEmail>({
        send_email: true, email_subject: '', email_message: '', demarche_template_id: '', demarche_template_ids_suivants: [],
    });
    const etapePlaquette = transition.documentKind === 'plaquette';

    // Objet, message et Demarche repris du reglage de Parametres > Notifications. Ils partaient vides a
    // chaque dossier : les textes gris de l'ecran n'etaient que des exemples, pas des valeurs, et le
    // commercial retapait tout au telephone. Demande d'Emeni (2026-08-29). Le texte reste modifiable ici
    // pour un client en particulier — le reglage donne le point de depart, pas le dernier mot.
    React.useEffect(() => {
        if (!etapePlaquette) return;
        let annule = false;
        moduleApi.getClientNotificationSettings()
            .then((s: any) => {
                if (annule || !s) return;
                setCourriel((prev) => ({
                    ...prev,
                    // Ne jamais ecraser ce qui a deja ete saisi : le reglage arrive apres le premier rendu.
                    email_subject: prev.email_subject || s.plaquette_send_subject || '',
                    email_message: prev.email_message || s.plaquette_send_body || '',
                    demarche_template_id: prev.demarche_template_id || s.plaquette_default_template_id || '',
                }));
            })
            .catch(() => {});
        return () => { annule = true; };
    }, [etapePlaquette]);

    // L'envoi du devis est la seconde étape qui parle au client. Coché par défaut, comme la plaquette :
    // envoyer le devis EST le geste de cette étape. Décocher sert au cas où il est déjà parti d'une
    // messagerie — le dossier le conserve alors sans lien de signature, et le signé se téléversera à la
    // main comme avant.
    const [courrielDevis, setCourrielDevis] = React.useState<DevisEmail>({
        send_email: true, email_subject: '', email_message: '',
    });
    const etapeDevis = transition.documentKind === 'devis';

    // LE BRIEF S'AFFICHE DANS CE PANNEAU (2026-08-31, repris tel quel dans la refonte plein écran) :
    // demande d'Emeni de « rester sur la même page de suivi et pouvoir tout faire d'ici pour éviter de
    // perdre le fil ». Un seul clic enregistre les réponses ET fait avancer le dossier (cf. confirmer() plus
    // bas) ; l'onglet Brief reste disponible tel quel pour le rouvrir plus tard, en dehors de cette étape.
    const [briefTemplate, setBriefTemplate] = React.useState<{ id: string; questions: BriefQuestion[] } | null>(null);
    const [briefValeurs, setBriefValeurs] = React.useState<Record<string, string>>({});
    const [briefPreremplies, setBriefPreremplies] = React.useState(0);
    const [briefChargement, setBriefChargement] = React.useState(!!transition.embedsBrief);
    const [clientDemarcheNom, setClientDemarcheNom] = React.useState('');
    const [clientReponses, setClientReponses] = React.useState<{ id: string; question: string; answer: string }[]>([]);
    React.useEffect(() => {
        if (!transition.embedsBrief || !dossierId) return;
        let annule = false;
        moduleApi.getDossierBrief(dossierId)
            .then((r: any) => {
                if (annule) return;
                setBriefTemplate(r?.template || null);
                const map: Record<string, string> = {};
                (r?.answers || []).forEach((a: any) => { map[a.id] = a.answer || ''; });
                setBriefValeurs(map);
                setBriefPreremplies(Number(r?.prefilled_count) || 0);
                setClientDemarcheNom(r?.client_demarche_name || '');
                setClientReponses(Array.isArray(r?.client_answers) ? r.client_answers : []);
            })
            .finally(() => { if (!annule) setBriefChargement(false); });
        return () => { annule = true; };
    }, [transition.embedsBrief, dossierId]);
    const modifierBrief = (id: string, valeur: string) => setBriefValeurs((prev) => ({ ...prev, [id]: valeur }));

    // UNE ÉTAPE NE GARDE PAS LE DOCUMENT NI LES SAISIES DE LA PRÉCÉDENTE. Le panneau reste monté d'une
    // étape à l'autre : après avoir envoyé la plaquette, l'écran du devis affichait encore
    // « plaquette.pdf » comme document choisi, et un clic de plus l'envoyait au client comme devis.
    // Vu en simulant un dossier de bout en bout le 18/09.
    React.useEffect(() => {
        setFile(null);
        setFileError('');
        setValues({});
    }, [transition.nextStage]);

    const champs = transition.requiresFields || [];
    const champsManquants = champs.some((f) => !(values[f.name] || '').trim());
    const documentManquant = transition.requiresDocument && !file;
    // Envoyer sans avoir choisi la Démarche produirait un email dont le bouton ne mène nulle part.
    const demarcheManquante = etapePlaquette && courriel.send_email && !courriel.demarche_template_id;
    const [briefErreur, setBriefErreur] = React.useState('');
    const bloque = champsManquants || documentManquant || demarcheManquante || !!fileError || saving || reading || briefChargement;

    const lireFichier = async (payload: Record<string, any>) => {
        if (!file) return payload;
        setReading(true);
        try {
            // Lu en base64 côté navigateur, comme les autres dépôts de documents de l'application — il n'y a
            // pas de stockage objet dans ce projet (R2 écarté, décision d'Emeni 2026-08-26).
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
                reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
                reader.readAsDataURL(file);
            });
            return { ...payload, document_base64: base64, document_file_name: file.name, document_mime_type: file.type || 'application/pdf' };
        } finally {
            setReading(false);
        }
    };

    const confirmer = async () => {
        // Le brief s'enregistre AVANT de faire avancer le dossier — si l'enregistrement échoue, l'étape ne
        // doit pas être franchie sur des réponses perdues (même logique que la plaquette : rien n'avance si
        // l'écriture qui compte a échoué).
        if (transition.embedsBrief && dossierId && briefTemplate) {
            setBriefErreur('');
            try {
                const answers = briefTemplate.questions.map((q) => ({
                    id: q.id, question: q.question, type: q.type, answer: briefValeurs[q.id] || '',
                }));
                await moduleApi.saveDossierBrief(dossierId, { template_id: briefTemplate.id, answers });
            } catch (e: any) {
                setBriefErreur(e?.message || "Le brief n'a pas pu être enregistré.");
                return;
            }
        }
        let payload: Record<string, any> = { to_stage: transition.nextStage };
        champs.forEach((f) => { payload[f.name] = values[f.name] || ''; });
        if (etapePlaquette) Object.assign(payload, courriel);
        if (etapeDevis) Object.assign(payload, courrielDevis);
        payload = await lireFichier(payload);
        onConfirm(payload);
    };

    // DEVIS ACCEPTÉ HORS PLATEFORME : petit formulaire à part (montant si besoin, exemplaire facultatif).
    // Le serveur applique la même règle (`accepte_hors_plateforme`) et reste seul juge.
    const [accepteOuvert, setAccepteOuvert] = React.useState(false);
    const [accepteMontant, setAccepteMontant] = React.useState('');
    const [accepteFichier, setAccepteFichier] = React.useState<File | null>(null);
    const [accepteErreurFichier, setAccepteErreurFichier] = React.useState('');
    React.useEffect(() => {
        setAccepteOuvert(false); setAccepteMontant(''); setAccepteFichier(null); setAccepteErreurFichier('');
    }, [transition.nextStage]);
    const accepteChamps = accepteTransition?.requiresFields || [];
    const accepteBloque = (accepteChamps.length > 0 && !accepteMontant.trim()) || !!accepteErreurFichier || saving || reading;
    const confirmerAccepte = async () => {
        if (!accepteTransition) return;
        let payload: Record<string, any> = { to_stage: accepteTransition.nextStage, accepte_hors_plateforme: true };
        if (accepteChamps.length > 0) payload.devis_amount_ttc = accepteMontant;
        if (accepteFichier) {
            setReading(true);
            try {
                const base64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
                    reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
                    reader.readAsDataURL(accepteFichier);
                });
                payload = { ...payload, document_base64: base64, document_file_name: accepteFichier.name, document_mime_type: accepteFichier.type || 'application/pdf' };
            } catch (e: any) {
                setAccepteErreurFichier(e?.message || 'Lecture du fichier impossible.');
                return;
            } finally {
                setReading(false);
            }
        }
        onConfirm(payload);
    };

    const passer = () => {
        if (!skipTransition) return;
        onConfirm({ to_stage: skipTransition.nextStage, skip: true });
    };

    // Ce que dit le bandeau : le verbe de l'étape, puis pourquoi (la note du rulebook) — et, quand le
    // dossier en est à la collecte des menus, le vrai blocage chiffré fourni par la page.
    return (
        <div className="rounded-xl border border-state-warn/30 bg-state-warn-soft overflow-hidden">
            <div className="px-[15px] pt-3.5 pb-4 space-y-3.5">
                <div className="flex items-start gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-lg bg-surface border border-state-warn/30 text-state-warn flex items-center justify-center"><AlertCircle size={14} /></span>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[.09em] text-state-warn">Ce qui bloque l'étape en cours</div>
                        <h2 className="text-[13px] font-bold text-state-warn mt-0.5">{transition.actionLabel}</h2>
                        <p className="text-[12.5px] leading-[1.5] text-state-warn text-pretty mt-[3px]">
                            {blocage || (apercuDevisSigne ? null : transition.hint)}
                            {!blocage && <span className="block mt-0.5 opacity-80"><span className="font-semibold">{label}</span> passe au stade « {PIPELINE_STAGE_LABELS[transition.nextStage]} ».</span>}
                        </p>
                    </div>
                    {actionBlocage}
                </div>

                {accepteTransition && (
                    <div className="rounded-[10px] border border-border bg-surface p-3.5 space-y-3">
                        {!accepteOuvert ? (
                            <div className="flex items-center gap-3 flex-wrap">
                                <p className="flex-1 min-w-[200px] text-[12.5px] leading-[1.5] text-content-muted">
                                    Le devis est déjà parti hors de la plateforme et le client l'a accepté ?
                                </p>
                                <button type="button" onClick={() => setAccepteOuvert(true)} disabled={saving} className={pillSecondary(34)}>
                                    <CheckCircle2 size={13} /> {accepteTransition.actionLabel}
                                </button>
                            </div>
                        ) : (
                            <>
                                <p className="text-[12.5px] leading-[1.5] text-content-muted">{accepteTransition.hint}</p>
                                {accepteChamps.map((f) => (
                                    <Field key={f.name} label={f.label} required>
                                        {({ id }) => <Input id={id} type="number" value={accepteMontant} onChange={(e) => setAccepteMontant(e.target.value)} />}
                                    </Field>
                                ))}
                                <label className="block cursor-pointer">
                                    <input type="file" accept="application/pdf,image/*" className="hidden"
                                        onChange={(e) => {
                                            const choisi = e.target.files?.[0] || null;
                                            if (choisi && choisi.size > 10000000) {
                                                setAccepteErreurFichier(`Ce document pèse ${(choisi.size / 1048576).toFixed(1)} Mo — 10 Mo maximum.`);
                                                setAccepteFichier(null);
                                                return;
                                            }
                                            setAccepteErreurFichier('');
                                            setAccepteFichier(choisi);
                                        }} />
                                    <div className="border border-dashed border-border hover:border-content hover:bg-surface-soft rounded-[10px] p-3 flex items-center gap-2 transition-colors">
                                        {accepteFichier ? <FileText size={16} className="text-content" /> : <Upload size={16} className="text-content-muted" />}
                                        <span className="text-[12.5px] font-semibold text-content">
                                            {accepteFichier ? accepteFichier.name : 'Joindre le devis signé ou la réponse du client (facultatif)'}
                                        </span>
                                    </div>
                                </label>
                                {accepteErreurFichier && <p className="text-[12px] text-state-danger font-semibold">{accepteErreurFichier}</p>}
                                <div className="flex items-center justify-end gap-2">
                                    <button type="button" onClick={() => setAccepteOuvert(false)} disabled={saving} className={pillGhost(34)}>Annuler</button>
                                    <button type="button" onClick={confirmerAccepte} disabled={accepteBloque} className={pillPrimary(34)}>
                                        {saving || reading ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                                        Confirmer : devis accepté
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {apercuDevisSigne && <DevisSigneApercu dossierId={apercuDevisSigne.dossierId} document={apercuDevisSigne.document} />}

                {(champs.length > 0 || transition.requiresDocument || etapePlaquette || etapeDevis || transition.embedsBrief) && (
                    <div className="rounded-[10px] border border-border bg-surface p-3.5 space-y-3.5">
                        {champs.map((f) => (
                            <Field key={f.name} label={f.label} required>
                                {({ id }) => (
                                    <Input id={id} type={f.type === 'number' ? 'number' : 'text'} value={values[f.name] || ''}
                                        onChange={(e) => setValues((prev) => ({ ...prev, [f.name]: e.target.value }))} />
                                )}
                            </Field>
                        ))}

                        {transition.requiresDocument && (
                            <label className="block cursor-pointer">
                                <input type="file" accept="application/pdf,image/*" className="hidden"
                                    onChange={(e) => {
                                        const choisi = e.target.files?.[0] || null;
                                        if (choisi && choisi.size > 10000000) {
                                            setFileError(`Ce document pèse ${(choisi.size / 1048576).toFixed(1)} Mo — au-delà de 10 Mo, la plupart des messageries refusent la pièce jointe et le client ne recevrait rien. Compressez-le puis réessayez.`);
                                            setFile(null);
                                            return;
                                        }
                                        setFileError('');
                                        setFile(choisi);
                                    }} />
                                <div className={`border border-dashed rounded-[10px] p-5 flex flex-col items-center justify-center gap-1 transition-colors ${file ? 'border-content bg-surface-soft' : 'border-border hover:border-content hover:bg-surface-soft'}`}>
                                    {file ? (
                                        <>
                                            <FileText size={22} className="text-content" />
                                            <p className="text-[12.5px] font-semibold text-content">{file.name}</p>
                                            <p className="font-mono text-[10.5px] text-content-muted">{(file.size / 1024).toFixed(0)} Ko</p>
                                        </>
                                    ) : (
                                        <>
                                            <Upload size={22} className="text-content-muted" />
                                            <p className="text-[12.5px] font-semibold text-content">Choisir le document</p>
                                            <p className="text-[11px] text-content-muted">Obligatoire pour cette étape — 10 Mo maximum</p>
                                        </>
                                    )}
                                </div>
                            </label>
                        )}

                        {etapePlaquette && <PlaquetteEmailFields valeur={courriel} onChange={setCourriel} destinataire={destinataire} destinataireEstReferent={destinataireEstReferent} />}
                        {etapeDevis && <DevisEmailFields valeur={courrielDevis} onChange={setCourrielDevis} destinataire={destinataire} destinataireEstReferent={destinataireEstReferent} />}

                        {transition.embedsBrief && (
                            briefChargement ? (
                                <div className="flex justify-center py-6"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
                            ) : !briefTemplate ? (
                                <div className="space-y-3">
                                    <ClientDemarcheAnswers demarcheName={clientDemarcheNom} answers={clientReponses} />
                                    <p className="text-[12px] leading-[1.5] text-content-muted p-2.5 rounded-[10px] bg-surface-soft border border-border-soft">
                                        Aucun questionnaire de brief n'est choisi pour ce dossier — il avancera sans réponses
                                        enregistrées. Choisissez-en un dans la section Brief du dossier (ou créez-en un depuis
                                        le bouton « Paramètres » de l'écran Suivi privatisation).
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <ClientDemarcheAnswers demarcheName={clientDemarcheNom} answers={clientReponses} />
                                    {briefPreremplies > 0 && (
                                        <p className="text-[12px] text-content-muted flex items-center gap-1.5 p-2 rounded-[10px] bg-surface-soft border border-border-soft">
                                            <MailCheck size={12} className="shrink-0" />
                                            {briefPreremplies === 1
                                                ? 'Une réponse a été reprise du questionnaire du client — vérifiez-la avant de confirmer.'
                                                : `${briefPreremplies} réponses ont été reprises du questionnaire du client — vérifiez-les avant de confirmer.`}
                                        </p>
                                    )}
                                    <BriefQuestionsForm questions={briefTemplate.questions} valeurs={briefValeurs} onChange={modifierBrief} />
                                </div>
                            )
                        )}
                    </div>
                )}

                {demarcheManquante && !error && (
                    <p className="text-[12px] text-state-warn font-semibold">Choisissez la Démarche vers laquelle le bouton du mail renverra le client.</p>
                )}
                {documentManquant && !error && (
                    <p className="text-[12px] text-state-warn font-semibold">Cette étape ne peut pas être validée sans son document.</p>
                )}
                {fileError && <p className="text-[12px] text-state-danger font-semibold">{fileError}</p>}
                {briefErreur && <p className="text-[12px] text-state-danger font-semibold">{briefErreur}</p>}
                {error && <p className="text-[12px] text-state-danger font-semibold">{error}</p>}
            </div>

            {/* Précédent / Passer / Suivant — l'action principale reste attachée aux champs qu'elle valide
                (décision d'Emeni, 16/09) ; l'abandon du dossier vit dans le pied de fiche. */}
            <div className="border-t border-state-warn/30 bg-surface px-[15px] py-3 flex items-center justify-end gap-2 flex-wrap">
                {backLabel && onBack && (
                    <button type="button" onClick={onBack} disabled={saving} className={`${pillGhost(34)} mr-auto`}>
                        <Undo2 size={13} /> Précédent — {backLabel}
                    </button>
                )}
                {skipTransition && (
                    <button type="button" onClick={passer} disabled={saving} className={pillSecondary(34)}>
                        <SkipForward size={13} /> Passer cette étape
                    </button>
                )}
                <button type="button" onClick={confirmer} disabled={bloque} className={pillPrimary(34)}>
                    {saving || reading ? <Loader2 size={13} className="animate-spin" /> : null}
                    {saving ? 'Enregistrement...' : reading ? 'Lecture du fichier...' : <>{transition.actionLabel} <ArrowRight size={13} /></>}
                </button>
            </div>
        </div>
    );
}
